"use client";

import { Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "@/modern/utils";
import type { Persona } from "../context/AnalyzeFileContext";
import { PersonaAvatar } from "./PersonaAvatar";
import { AdvisorAccentBar } from "./AdvisorAccentBar";

type AnalyzeFeedStickyHeaderProps = {
  persona: Persona;
  insightCount: number;
  isGenerating: boolean;
  /** All tab: header tracks whichever advisor is at the top while scrolling. */
  isScrollLinked?: boolean;
};

export const AnalyzeFeedStickyHeader = ({
  persona,
  insightCount,
  isGenerating,
  isScrollLinked = false,
}: AnalyzeFeedStickyHeaderProps) => (
  <div
    className={cn(
      "sticky top-0 z-10 mb-1.5 border-b border-border/70 bg-card/95 backdrop-blur-md supports-[backdrop-filter]:bg-card/80",
    )}
  >
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={persona.id}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 2 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        className="flex gap-0"
      >
        <AdvisorAccentBar personaId={persona.id} />
        <div className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-1.5">
          <div className="shrink-0 scale-[0.82] origin-center">
            <PersonaAvatar persona={persona} size="md" presentation="chip" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <p className="text-[13px] font-semibold text-foreground truncate leading-tight">
                {persona.name}
              </p>
              {isGenerating && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                  Analyzing
                </span>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground truncate leading-tight">
              {persona.description}
            </p>
          </div>
          <div className="shrink-0 text-right">
            {insightCount > 0 && (
              <p className="text-[10px] font-medium tabular-nums text-muted-foreground leading-tight">
                {insightCount} insight{insightCount !== 1 ? "s" : ""}
              </p>
            )}
            {isScrollLinked && (
              <p className="text-[9px] text-muted-foreground/80 leading-none mt-0.5">Reading</p>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  </div>
);
